import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '../store/useAuthStore';
import { friendlyErrorMessage } from '../utils/errorMessage';
import { trackApiFailure, trackClarityEvent } from '../lib/observability';
import { API_BASE } from '../config/runtimeConfig';

export const API_URL = API_BASE;

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ATTACH BEARER TOKEN ON EVERY AUTHENTICATED REQUEST.
// The frontend and API are on different origins (us-central1 vs asia-southeast1), which are
// cross-site. The HttpOnly access_token cookie is SameSite=Lax, so the browser never sends it
// on these cross-site XHRs — every authenticated call would 401. The API's JwtBearer also reads
// the Authorization header, so we send the in-memory access token here for both normal and
// impersonated sessions. (Proper long-term fix: set the auth cookies SameSite=None;Secure.)
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// HANDLE 401 UNAUTHORIZED (EXPIRED TOKENS)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Safe (normalized) request descriptor for Clarity — relative path, never a payload.
    const reqMethod = (originalRequest?.method ?? 'get').toUpperCase();
    const reqPath = originalRequest?.url ?? '';
    const isTimeout = error?.code === 'ECONNABORTED';

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      trackApiFailure({ method: reqMethod, path: reqPath, status: 401 });

      try {
        // Try refresh via cookie — the response contains the new access token
        const refreshResponse = await axios.post<{ data: { accessToken: string } }>(
          `${API_URL}/auth/refresh`, {}, { withCredentials: true }
        );
        // Update in-memory token so subsequent Authorization headers use the fresh token
        const newAccessToken = refreshResponse.data?.data?.accessToken;
        if (newAccessToken) {
          useAuthStore.getState().setTokens(newAccessToken, null);
        }
        trackClarityEvent('api-retry-succeeded');
        return api(originalRequest);
      } catch (refreshError) {
        console.error('[API] Refresh failed. Logging out...', refreshError);
        trackClarityEvent('auth-session-expired');
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const status = error?.response?.status;
    if (status && status >= 400 && status < 600 && status !== 401) {
      trackApiFailure({ method: reqMethod, path: reqPath, status });
      // Sanitise so raw server text (SQL/constraint/stack) never reaches the user.
      toast.error(friendlyErrorMessage(error, `Request failed (${status}).`));
    } else if (!error?.response) {
      trackApiFailure({ method: reqMethod, path: reqPath, isTimeout });
      toast.error('Network error. Check your connection.');
    }

    return Promise.reject(error);
  }
);

export default api;
